import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faTimes,
    faHistory,
    faInfoCircle,
    faArrowRight
} from '@fortawesome/free-solid-svg-icons';
import { getApprovalBadge } from '../../utils/crmStatusMapper';
import './ApprovalStatusModal.css';

/**
 * Read-only approval-status viewer.
 *
 * Profile approval status is now driven exclusively by the KYC portal review
 * (approve/reject a KYC case) — it is no longer changed here. This modal shows
 * the current status (with the grandfathered "Approved*" marker), a one-line
 * note explaining that KYC drives it, and the full approval history.
 */
const ApprovalStatusModal = ({ isOpen, onClose, profile }) => {
    if (!isOpen || !profile) return null;

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const badge = getApprovalBadge(profile);
    const currentStatus = badge.status;
    const approvalLogs = profile.approval_logs || [];

    return (
        <div className="approval-modal-overlay" onClick={handleOverlayClick}>
            <div className="approval-modal">
                {/* Header */}
                <div className="approval-modal-header">
                    <div className="approval-modal-header-content">
                        <h3>Approval Status</h3>
                        <div className="approval-profile-name">{profile.legal_entity_name}</div>
                    </div>
                    <button
                        className="approval-close-button"
                        onClick={onClose}
                        type="button"
                    >
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>

                {/* Body */}
                <div className="approval-modal-body">
                    {/* Current Status */}
                    <div className="approval-current-status">
                        <h4>Current Status</h4>
                        <span
                            className={`approval-current-status-value ${currentStatus}`}
                            title={badge.title}
                        >
                            {badge.label}
                        </span>
                    </div>

                    {/* KYC-driven note */}
                    <div className="approval-kyc-note">
                        <FontAwesomeIcon icon={faInfoCircle} />
                        <span>
                            Approval status is driven by the KYC review. Approve or reject the
                            partner's KYC case to change it.
                        </span>
                    </div>

                    {badge.isGrandfathered && (
                        <div className="approval-kyc-note approval-kyc-note-grandfathered">
                            <FontAwesomeIcon icon={faInfoCircle} />
                            <span>{badge.title}</span>
                        </div>
                    )}

                    {/* Audit Trail */}
                    <div className="approval-audit-trail">
                        <h4>
                            <FontAwesomeIcon icon={faHistory} />
                            Approval History
                        </h4>
                        {approvalLogs.length === 0 ? (
                            <div className="approval-audit-empty">
                                No approval history available
                            </div>
                        ) : (
                            approvalLogs.map((log) => (
                                <div key={log.id} className="approval-audit-item">
                                    <div className="approval-audit-header">
                                        <div className="approval-audit-status-change">
                                            <span className={`approval-audit-status-badge ${log.previous_status}`}>
                                                {log.previous_status}
                                            </span>
                                            <FontAwesomeIcon icon={faArrowRight} className="approval-audit-arrow" />
                                            <span className={`approval-audit-status-badge ${log.new_status}`}>
                                                {log.new_status}
                                            </span>
                                        </div>
                                        <div className="approval-audit-meta">
                                            <div className="approval-audit-user">
                                                {log.changed_by_name}
                                                {log.changed_by_employee_id && ` (${log.changed_by_employee_id})`}
                                            </div>
                                            <div className="approval-audit-date">
                                                {formatDate(log.changed_at)}
                                            </div>
                                        </div>
                                    </div>
                                    {(log.rejection_reason || log.notes) && (
                                        <div className="approval-audit-details">
                                            {log.rejection_reason && (
                                                <p>
                                                    <span className="approval-audit-label">Rejection Reason:</span>
                                                    {log.rejection_reason}
                                                </p>
                                            )}
                                            {log.notes && (
                                                <p>
                                                    <span className="approval-audit-label">Notes:</span>
                                                    {log.notes}
                                                </p>
                                            )}
                                        </div>
                                    )}
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ApprovalStatusModal;
